import * as yup from "yup";
import moment from "moment";
import { minValidDate } from "../../DataEntry/Grid/gridFunctions";

export const createFormLiteValidationSchema = (contextValues = {}) => {
  const { totalPages, jobData, isSubJob, isFirstJob } = contextValues;

  return yup.object().shape({
     overall_page_from: yup.string()
      .when("qdx_status", {
        is: (val) => val !== "INCOMPLETE",
        then: (schema) =>
          jobData?.client_level_customization?.overall_page_from
          ? schema
          .required("Invalid Page Number")
          .test("overall_page_from_validation", "Invalid Page Number", function(value) {
            const { overall_page_to } = this.parent;
            const { totalPages } = this.options.context || {};
            if(!jobData?.client_level_customization?.overall_page_from){return true }
            // Check if value is empty, zero, or invalid
            if (!value || value <= 0) {
              return false;
            }
            
            // Check if exceeds total pages
            if (value > totalPages) {
              return false;
            }
            
            // Check if from is greater than to
            if (overall_page_to && parseInt(value) > parseInt(overall_page_to)) {
              return false;
            }
            
            return true;
          })
          :schema
      }),

    overall_page_to: yup.string()
      .when("qdx_status", {
        is: (val) => val !== "INCOMPLETE",
        then: (schema) =>jobData?.client_level_customization?.overall_page_from
        ? schema
          .required("Invalid Page Number")
          .test("overall_page_to_validation", "Invalid Page Number", function(value) {
            const { overall_page_from } = this.parent;
            const { totalPages } = this.options.context || {};
            if(!jobData?.client_level_customization?.overall_page_to){return true }
            // Check if value is empty, zero, or invalid
            if (!value || value <= 0) {
              return false;
            }
            
            // Check if exceeds total pages  
            if (value > totalPages) {
              return false;
            }
            
            // Check if from is greater than to
            if (overall_page_from && parseInt(overall_page_from) > parseInt(value)) {
              return false;
            }
            
            return true;
          })
          :schema
      }),
    qdx_status: yup.string().when("$isAdditional", {
      is: true,
      then: (schema) => schema.when("$isAdditionalInfoOptions.status", {
        is: true,
        then: (schema) => schema.required("Required")
      })
    }),
    
    qdx_reason: yup
      .array()
      .of(yup.number().typeError("Must be a number"))
      .when(["qdx_status", "$isAdditional"], {
        is: (status, isAdditional) => isAdditional && status === "INCOMPLETE",
        then: (schema) =>
          schema.when("$isAdditionalInfoOptions.reason", {
            is: true,
            then: (schema) =>
              schema.min(1, "At least one reason is required"), // must contain numbers
            otherwise: (schema) => schema, // allow empty array
          }),
        otherwise: (schema) => schema, // allow empty array
      }),

    // payer_name: yup.string().when("qdx_status", {
    //   is: (val) => val !== "INCOMPLETE",
    //   then: (schema) => schema.when(["$isSubJob", "$isFirstJob"], {
    //     is: (isSubJob, isFirstJob) => (isSubJob && isFirstJob) || (!isSubJob && !isFirstJob),
    //     then: (schema) => schema.required("Required")
    //   })
    // }),

    payment_method: yup.string().when("qdx_status", {
      is: (val) => val !== "INCOMPLETE",
      then: (schema) => schema.when("$isSubJob", {
        is: (isSubJob) => isSubJob !== true,
        then: (schema) => schema.required("Required"),
      })
    }),

    check_number: yup.string().when("qdx_status", {
      is: (val) => val !== "INCOMPLETE",
      then: (schema) => schema.when("$isSubJob", {
        is: (isSubJob) => isSubJob !== true,
        then: (schema) => schema.required("Required"),
      })
    }),

    check_amount: yup.string()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .typeError("Required")
      .nullable()
      .test("check_amount_validation", function (value) {
        const { payment_method, qdx_status } = this.parent;
        
        // Skip validation if status is INCOMPLETE
        if (qdx_status === "INCOMPLETE") {
          return true;
        }
        
        const zeroAmount = parseFloat(0).toFixed(2);
        const checkAmountFloat = parseFloat(value || 0).toFixed(2);

        if (value === undefined || value === null) {
          return this.createError({ message: "Required" });
        }

        if (checkAmountFloat === zeroAmount && payment_method !== "NON") {
          return this.createError({ message: "Invalid Selection" });
        }

        if (
          parseFloat(checkAmountFloat) > parseFloat(zeroAmount) &&
          !["CHK", "ACH", "VCP"].includes(payment_method)
        ) {
          return this.createError({ message: "Invalid Selection" });
        }

        if (value < 0) {
          return this.createError({ message: "negative value" });
        }

        return true;
      }),

    check_date: yup.string().test("check_date_validation", function (value) {
      const { qdx_status } = this.parent;
      
      // Skip validation if status is INCOMPLETE
      if (qdx_status === "INCOMPLETE") {
        return true;
      }
      
      const cleanDate = value?.replace("__/__/____", "");

      if (!cleanDate) {
        return this.createError({ message: "Required" });
      }

      if (
        !moment(cleanDate, "MM/DD/YYYY", true).isValid() ||
        !moment(cleanDate, "MM/DD/YYYY", true).isAfter(minValidDate)
      ) {
        return this.createError({ message: "Invalid date" });
      }

      return true;
    }),

    aba_routing_number: yup.string().test("aba_routing_validation", function (value) {
      const { payment_method, qdx_status } = this.parent;
      const isAbaEnabled = jobData?.client_level_customization?.is_aba_routing_number;
      
      // Skip validation if status is INCOMPLETE or feature not enabled
      if (qdx_status === "INCOMPLETE" || !isAbaEnabled) {
        return true;
      }

      if (!value && (payment_method === "CHK" || !payment_method)) {
        return this.createError({ message: "Invalid Selection" });
      }

      if (value && payment_method !== "CHK") {
        return this.createError({ message: "Invalid Selection" });
      }

      return true;
    }),

    payer_account_number: yup.string().test("payer_account_validation", function (value) {
      const { payment_method, qdx_status } = this.parent;
      const isPayerAccountEnabled = jobData?.client_level_customization?.is_payer_account_number;
      
      // Skip validation if status is INCOMPLETE or feature not enabled
      if (qdx_status === "INCOMPLETE" || !isPayerAccountEnabled) {
        return true;
      }

      if (!value && (payment_method === "CHK" || !payment_method)) {
        return this.createError({ message: "Invalid Selection" });
      }

      if (value && payment_method !== "CHK") {
        return this.createError({ message: "Invalid Selection" });
      }

      return true;
    }),

    remaining_balance: yup.string()
      .nullable()
      .test("remaining_balance_zero", "Required", function (value) {
        const { qdx_status } = this.parent;
        const isSubJob = this.options.context?.isSubJob;
        
        // Skip validation if status is INCOMPLETE or is sub job
        if (qdx_status === "INCOMPLETE" || isSubJob) {
          return true;
        }
        
        return parseFloat(value || 0).toFixed(2) === parseFloat(0).toFixed(2);
      }),
    
    payer_page_from: yup.string().when(["qdx_status", "$isPayerPage"], {
      is: (status, isPayerPage) => status !== "INCOMPLETE" && isPayerPage,
      then: (schema) => schema.required("Invalid Page Number").test("payer_page_from_validation", "Invalid Page Number", function(value) {
        const { payer_page_to } = this.parent;
        const { totalPages } = this.options.context || {};
        
        if (!value || value <= 0 || value > totalPages || value > payer_page_to) {
          return false;
        }
        return true;
      }),
    }),
    
    payer_page_to: yup.string().when(["qdx_status", "$isPayerPage"], {
      is: (status, isPayerPage) => status !== "INCOMPLETE" && isPayerPage,
      then: (schema) => schema.required("Invalid Page Number").test("payer_page_to_validation", "Invalid Page Number", function(value) {
        const { payer_page_from } = this.parent;
        const { totalPages } = this.options.context || {};
        
        if (!value || value <= 0 || value > totalPages || payer_page_from > value) {
          return false;
        }
        return true;
      }),
    }),
    
    payee_page_from: yup.string().when(["qdx_status", "$isPayeePage"], {
      is: (status, isPayeePage) => status !== "INCOMPLETE" && isPayeePage,
      then: (schema) => schema.required("Invalid Page Number").test("payee_page_from_validation", "Invalid Page Number", function(value) {
        const { payee_page_to } = this.parent;
        const { totalPages } = this.options.context || {};
        
        if (!value || value <= 0 || value > totalPages || value > payee_page_to) {
          return false;
        }
        return true;
      }),
    }),
    
    payee_page_to: yup.string().when(["qdx_status", "$isPayeePage"], {
      is: (status, isPayeePage) => status !== "INCOMPLETE" && isPayeePage,
      then: (schema) => schema.required("Invalid Page Number").test("payee_page_to_validation", "Invalid Page Number", function(value) {
        const { payee_page_from } = this.parent;
        const { totalPages } = this.options.context || {};
        
        if (!value || value <= 0 || value > totalPages || payee_page_from > value) {
          return false;
        }
        return true;
      }),
    }),
    // PLB Adjustments validation
    plb_adjustments: yup.array().when("qdx_status", {
    is: (val) => val !== "INCOMPLETE",
    then: (schema) => schema.of(
        yup.object().shape({
        reason: yup.string().required("select a valid option"),
        
        plb_amt: yup.string()
            .required("Select a valid option")
            .test("not_empty_or_zero", "Select a valid option", function(value) {
            if (!value || value === "" || value === null || parseFloat(value).toFixed(2) === parseFloat(0).toFixed(2)) {
                return false;
            }
            return true;
            })
            .test("valid_amount", "Must be a valid amount", function(value) {
            if (value && !/^[+-]?[0-9]{1,9}(?:\.[0-9]{1,2})?$/.test(value)) {
                return false;
            }
            return true;
            }),

        npi: yup.string()
            .required("required")
            .test("npi_format", "Must be only digits", function(value) {
            if (value && value !== "" && !/^[0-9]{10}$/.test(value)) {
                return false;
            }
            return true;
            })
        })
    ),
    otherwise: (schema) => schema.optional()
    }),
    claims: yup.array().of(
      yup.object().shape({
        patient_account_number: yup.string().test("patient_account_number_validation", "Required", function(value) {
          const { qdx_status } = this.options?.from?.[1]?.value || {};
          
          if (qdx_status === "INCOMPLETE") return true;
          if (!value) return false;
          return true;
        }),

        page_from: yup.string()
          .test("page_from_validation", "Invalid Page Number", function (value) {
            const { qdx_status } = this.options?.from?.[1]?.value || {};
            
            // Skip validation if status is INCOMPLETE
            if (qdx_status === "INCOMPLETE") {
              return true;
            }
            
            const { page_to } = this.parent;
            const { totalPages } = this.options.context || {};
            if (!value || value <= 0 || value > totalPages || value > page_to) {
              return false;
            }
            return true;
          }),

        page_to: yup.string()
          .test("page_to_validation", "Invalid Page Number", function (value) {
            const { qdx_status } = this.options?.from?.[1]?.value || {};
            
            // Skip validation if status is INCOMPLETE
            if (qdx_status === "INCOMPLETE") {
              return true;
            }
            
            const { page_from } = this.parent;
            const { totalPages } = this.options.context || {};
            if (!value || value <= 0 || value > totalPages || page_from > value) {
              return false;
            }
            return true;
          }),

        service_lines: yup.array().of(
          yup.object().shape({
            billed: yup.string().test("billed_validation", "Required", function(value) {
              const { qdx_status } = this.options?.from?.[2]?.value || {};
              
              if (qdx_status === "INCOMPLETE") return true;
              if (!value) return false;
              return true;
            }),

            paid: yup.string().test("paid_validation", "Required", function(value) {
              const { qdx_status } = this.options?.from?.[2]?.value || {};
              
              if (qdx_status === "INCOMPLETE") return true;
              if (!value) return false;
              return true;
            }),

            period_start: yup.string().test("period_start_validation", function (value) {
              const { qdx_status } = this.options?.from?.[2]?.value || {};
              
              // Skip validation if status is INCOMPLETE
              if (qdx_status === "INCOMPLETE") {
                return true;
              }
              
              const cleanDate = value?.replace("__/__/____", "");

              if (!cleanDate) {
                return this.createError({ message: "Required" });
              }
              if (
                !moment(cleanDate, "MM/DD/YYYY", true).isValid() ||
                !moment(cleanDate, "MM/DD/YYYY", true).isAfter(minValidDate)
              ) {
                return this.createError({ message: "Invalid date" });
              }
              return true;
            }),

            period_end: yup.string().test("period_end_validation", function (value) {
              const { qdx_status } = this.options?.from?.[2]?.value || {};
              
              // Skip validation if status is INCOMPLETE
              if (qdx_status === "INCOMPLETE") {
                return true;
              }
              
              const { period_start } = this.parent;
              const cleanDate = value?.replace("__/__/____", "");

              if (!cleanDate) {
                return this.createError({ message: "Required" });
              }
              if (
                !moment(cleanDate, "MM/DD/YYYY", true).isValid() ||
                !moment(cleanDate, "MM/DD/YYYY", true).isAfter(minValidDate) ||
                !moment(cleanDate, "MM/DD/YYYY").isSameOrAfter(
                  moment(period_start, "MM/DD/YYYY", true)
                )
              ) {
                return this.createError({ message: "Invalid date" });
              }
              return true;
            }),

          })
        ),
      })
    ),
  });
};

export default createFormLiteValidationSchema;